import { useState } from 'react';
import { useGameState } from '../hooks/useGameState';
import { rollGacha } from '../domain/gacha';
import { acquireStone, sellDuplicate } from '../domain/economy';
import { StoneIcon } from '../components/StoneIcon';
import type { Screen } from '../App';
import type { Stone } from '../domain/types';

interface GachaScreenProps {
  onNavigate: (next: Screen, stoneId?: string) => void;
}

export function GachaScreen({ onNavigate }: GachaScreenProps) {
  const { state, setState, hasStone } = useGameState();
  const [result, setResult] = useState<Stone | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [duplicate, setDuplicate] = useState(false);

  const canPull = !state.daily.gachaUsed;

  const handlePull = () => {
    if (!canPull || spinning) return;
    setSpinning(true);
    setResult(null);
    setDuplicate(false);
    // 演出: 800ms 後に結果表示
    window.setTimeout(() => {
      const stone = rollGacha();
      setResult(stone);
      const isDuplicate = hasStone(stone.id);
      setDuplicate(isDuplicate);
      if (!isDuplicate) {
        setState((s) => acquireStone(s, stone.id));
      }
      // gachaUsedフラグは結果が新規でも重複でも立てる
      setState((s) => ({ ...s, daily: { ...s.daily, gachaUsed: true } }));
      setSpinning(false);
    }, 800);
  };

  const handleSellDuplicate = () => {
    if (!result) return;
    const gain = sellDuplicate(result.id);
    setState((s) => ({
      ...s,
      daily: { ...s.daily, energy: s.daily.energy + gain },
    }));
    setDuplicate(false);
    setResult(null);
  };

  return (
    <div className="screen gacha">
      <h1>今日のガチャ</h1>
      <p className="gacha__hint">排出率: 低ティア 70% / 中ティア 20% / レア 10%</p>

      <div className="gacha__stage">
        {spinning ? (
          <div className="gacha__spinner" aria-label="演出中">
            <div className="gacha__spinner-orb" />
            <p>…</p>
          </div>
        ) : result ? (
          <div className="gacha__result">
            <StoneIcon stone={result} stage={0} size={160} />
            <h2>{result.name}</h2>
            <p className="gacha__result-rarity">
              {result.rarity === 'low' && '低ティア'}
              {result.rarity === 'mid' && '中ティア'}
              {result.rarity === 'high' && '★ レア'}
            </p>
            {duplicate ? (
              <>
                <p>すでに所持している石です。</p>
                <button className="btn btn--primary" onClick={handleSellDuplicate}>
                  売却する(+{sellDuplicate(result.id)} エネルギー)
                </button>
              </>
            ) : (
              <p>コレクションに加わりました!</p>
            )}
          </div>
        ) : (
          <div className="gacha__empty">
            <p>下のボタンから今日の分を引こう</p>
          </div>
        )}
      </div>

      <button
        className="btn btn--primary btn--big"
        onClick={handlePull}
        disabled={!canPull || spinning}
        type="button"
      >
        {state.daily.gachaUsed ? '本日のガチャは使用済み' : 'ガチャを引く'}
      </button>

      {result && !duplicate && (
        <button
          className="btn"
          onClick={() => onNavigate('collection')}
          type="button"
        >
          コレクションを見る
        </button>
      )}
    </div>
  );
}
